import {
  type Assessment,
  type CriticalActionEvent,
  type CriticalSubActionEvent,
  type Flow,
  type PlayerHintEvent,
  type PlayerRun,
  type SceneModule,
  type ScoringRubric,
  type ScoringRunResult,
  type Stage,
  type SupplySelectionEvent,
  techniqueRationalesId
} from '@webapp2/shared/model'
import { moduleStages, toRunResult } from '@webapp2/shared/run-result'
import {
  bladderScanCriticalActionIds,
  contaminationCriticalActionIds,
  pointsPerContamination,
  pointsPerFailedBladderScan,
  pointsPerHint
} from '@webapp2/shared/score-deductions'
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { type DebriefRouteLocation } from '../router'
import { state } from '../state'
import {
  ensureLinkedModuleBuilds,
  ensureLinkedModuleBuildsByModuleBuildId,
  linkedModuleBuildsByModuleBuildId
} from './linked-module-builds-service'
import { currentUserCoursePerformance } from './course-service'
import { currentLinkedPlayerBuilds, ensureCurrentLinkedPlayerBuilds } from './linked-player-builds-service'

export async function handleRunEnd(runLog: RunLog) {
  try {
    await processRunLog(runLog)
  } catch (error) {
    console.error(`Error processing run log ${runLog.id}:`, error)
  }
}

function parseRunLog(runLog: RunLog): PlayerRun {
  const criticalActions: CriticalActionEvent[] = []
  const hints: PlayerHintEvent[] = []
  const supplySelectionsByCriticalActionId = new Map<string, SupplySelectionEvent>()
  const criticalSubActionsById = new Map<string, CriticalSubActionEvent>()

  let courseId: string | undefined
  let moduleBuildId: string | undefined
  let multiplayerSessionId: string | undefined
  let techniqueId = ''
  let isTest = false
  let completed = false
  let exitModule = false
  let time = 0

  for (const row of runLog.rows) {
    switch (row.type) {
      case 'RunLog':
        row.json.systemInfo.commandLineArgs.forEach((value, index, args) => {
          if (value === '-ms') {
            multiplayerSessionId = args[index + 1]
          }
          if (value === '-cid') {
            courseId = args[index + 1]!
          }
          if (value === '-b') {
            moduleBuildId = args[index + 1]!
          }
        })
        isTest = row.json.isTestMode
        techniqueId = row.json.techniqueId.toLocaleLowerCase()
        break
      case 'EventLog':
        switch (row.json.type) {
          case 'Hint':
            hints.push({ id: row.json.data.id, criticalAction: row.json.data.criticalAction })
            break
          case 'CriticalActionCompleted':
            criticalActions.push({ id: row.json.data.id, passed: row.json.data.passed })
            break
          case 'CriticalSubActionCompleted':
            criticalSubActionsById.set(`${row.json.data.parent}:${row.json.data.id}`, row.json.data)
            break
          case 'SupplySelectionVerified':
            supplySelectionsByCriticalActionId.set(row.json.data.id, row.json.data)
            break
          case 'RunComplete':
            completed = true
            time = row.json.time
            break
          case 'ExitModule':
            exitModule = true
            if (!completed) {
              time = row.json.time
            }
            break
          case 'TechniqueManager.OnApplicationQuit()':
            if (!completed) {
              time = row.json.time
            }
            break
          case 'Restart':
            criticalActions.length = 0
            hints.length = 0
            supplySelectionsByCriticalActionId.clear()
            criticalSubActionsById.clear()
            completed = false
            exitModule = false
            time = 0
            break
        }
        break
    }
  }

  return {
    id: runLog.id,
    criticalActions,
    hints,
    supplySelections: Array.from(supplySelectionsByCriticalActionId.values()),
    criticalSubActions: Array.from(criticalSubActionsById.values()),
    score: 0,
    baseScore: 0,
    passed: false,
    isTest,
    completed,
    time,
    hintsUsed: hints.length,
    failedContaminationCriticalActionCount: 0,
    failedBladderScanCriticalActionCount: 0,
    courseId,
    moduleBuildId,
    multiplayerSessionId,
    exitModule,
    techniqueId
  }
}

function calculateScores(playerRun: PlayerRun, stages: Stage[]) {
  const criticalActionsById = new Map(playerRun.criticalActions.map(x => [x.id, x]))

  let totalCriticalActions = 0
  let passedCriticalActions = 0

  for (const stage of stages) {
    for (const criticalAction of stage.criticalActions) {
      totalCriticalActions++
      const completed = criticalActionsById.get(criticalAction.id)
      if (completed?.passed) {
        passedCriticalActions++
      }
    }
  }

  const rawBaseScore = totalCriticalActions > 0 ? (passedCriticalActions / totalCriticalActions) * 100 : 0
  playerRun.baseScore = Math.round(rawBaseScore)

  const failedCriticalActions = [...criticalActionsById.values()].filter(x => !x.passed)
  playerRun.failedContaminationCriticalActionCount = failedCriticalActions.filter(x =>
    contaminationCriticalActionIds.has(x.id)
  ).length
  playerRun.failedBladderScanCriticalActionCount = failedCriticalActions.filter(x =>
    bladderScanCriticalActionIds.has(x.id)
  ).length

  const hintReduction = playerRun.isTest ? playerRun.hintsUsed * pointsPerHint : 0
  const contaminationReduction = playerRun.failedContaminationCriticalActionCount * pointsPerContamination
  const bladderScanReduction = playerRun.failedBladderScanCriticalActionCount * pointsPerFailedBladderScan

  const rawScore = Math.max(0, rawBaseScore - hintReduction - contaminationReduction - bladderScanReduction)
  playerRun.score = Math.round(rawScore)
  playerRun.passed = playerRun.isTest && playerRun.score >= 80
}

async function processRunLog(runLog: RunLog) {
  const playerRun = parseRunLog(runLog)

  if (!playerRun.techniqueId) {
    throw new Error(`Run processing requires technique for run: ${playerRun.id}`)
  }

  const module = await getRunModule(playerRun)
  const stages = module && moduleStages(module, state.techniqueRationales[techniqueRationalesId(playerRun.techniqueId)])
  if (!stages) {
    throw new Error(`Stages missing for run ${playerRun.id}`)
  }

  // Calculate scores
  calculateScores(playerRun, stages)

  const runResult = toRunResult(playerRun, stages)
  state.runResults[runResult.id] = runResult
}

export async function handleRunResult(
  runResult: ScoringRunResult,
  runId: string,
  launchData: {
    courseId?: string
    isTestMode: boolean
    multiplayerSessionId?: string
    build: { id?: string; techniqueId: string; linkedModuleBuildsId?: string }
  },
  sceneModules: SceneModule[]
) {
  const criticalActions: CriticalActionEvent[] = (runResult.criticalActionResults ?? []).map(x => ({
    id: x.id,
    passed: x.result === 'Passed'
  }))
  const completed =
    criticalActions.length > 0 && runResult.criticalActionResults.every(x => x.result !== 'NotAttempted')
  const hints: PlayerHintEvent[] = (runResult.hints ?? []).map((x, index) => ({
    id: index.toString(),
    criticalAction: x.criticalActionId
  }))

  const playerRun: PlayerRun = {
    id: runId,
    techniqueId: launchData.build.techniqueId.toLowerCase(),
    criticalActions,
    hints,
    supplySelections: [],
    criticalSubActions: [],
    score: Math.round(runResult.score),
    baseScore: Math.round(runResult.baseScore),
    passed: runResult.passed,
    isTest: launchData.isTestMode,
    completed,
    time: runResult.time,
    hintsUsed: runResult.hintsUsed,
    failedContaminationCriticalActionCount: 0,
    failedBladderScanCriticalActionCount: 0,
    courseId: launchData.courseId,
    multiplayerSessionId: launchData.multiplayerSessionId,
    moduleBuildId: launchData.build.id,
    linkedModuleBuildsId: launchData.build.linkedModuleBuildsId,
    criticalActionResults: runResult.criticalActionResults
  }

  const sceneModule = sceneModules.find(x => x.techniqueId.toLowerCase() === playerRun.techniqueId)
  const module = sceneModule?.flow ? sceneModule : await getRunModule(playerRun)
  const stages = module && moduleStages(module, state.techniqueRationales[techniqueRationalesId(playerRun.techniqueId)])
  if (!stages) {
    console.warn(`No stages found for run result, using empty stages`)
    state.runResults[runId] = toRunResult(playerRun, [])
    return
  }
  state.runResults[runId] = toRunResult(playerRun, stages, module?.assessments)
}

type RunModule = { flow?: Flow; rubric?: ScoringRubric; stages?: Stage[]; assessments?: Assessment[] }

async function getRunModule(run: {
  moduleBuildId?: string
  linkedModuleBuildsId?: string
  techniqueId: string
}): Promise<RunModule | undefined> {
  if (run.linkedModuleBuildsId) {
    await ensureLinkedModuleBuilds([run.linkedModuleBuildsId])
    return state.linkedModuleBuilds[run.linkedModuleBuildsId]
  }
  if (run.moduleBuildId) {
    await ensureLinkedModuleBuildsByModuleBuildId([run.moduleBuildId])
    return linkedModuleBuildsByModuleBuildId.value[run.moduleBuildId]
  }
  await ensureCurrentLinkedPlayerBuilds()
  return currentLinkedPlayerBuilds.value?.embeddedModules?.find(x => x.techniqueId.toLowerCase() === run.techniqueId)
}

export function useRunRoute() {
  const route = useRoute() as DebriefRouteLocation
  const runResult = computed(() => state.runResults[route.params.runId]!)
  const courseId = runResult.value.courseId!
  const userCoursePerformance = computed(() => currentUserCoursePerformance(courseId))
  return { route, runResult, userCoursePerformance }
}

export type Row<T> = {
  id: string
  type: string
  json: T
}

export type RunLogRow = Row<RunLogRowData> & { type: 'RunLog' }

export type RunLogRowData = {
  techniqueId: string
  systemInfo: SystemInfo
  isTestMode: boolean
}

export type SystemInfo = {
  commandLineArgs: string[]
}

export type EventLogRow<T, U extends string> = Row<EventLog<T> & { type: U }> & { type: 'EventLog' }

export type EventLog<T> = {
  id: string
  type: string
  chapterId: string
  time: number
  data: T
}

export type RunCompleteRow = EventLogRow<object, 'RunComplete'>
export type ExitModuleRow = EventLogRow<object, 'ExitModule'>
export type ApplicationQuitRow = EventLogRow<object, 'TechniqueManager.OnApplicationQuit()'>
export type RestartRow = EventLogRow<object, 'Restart'>

export type CriticalActionRow = EventLogRow<CriticalActionEvent, 'CriticalActionCompleted'>
export type CriticalSubActionRow = EventLogRow<CriticalSubActionEvent, 'CriticalSubActionCompleted'>

export type HintRow = EventLogRow<PlayerHintEvent & { criticalAction: string }, 'Hint'>

export type SupplySelectionVerifiedRow = EventLogRow<SupplySelectionEvent, 'SupplySelectionVerified'>

export type RunLog = {
  id: string
  rows: (
    | RunLogRow
    | HintRow
    | CriticalActionRow
    | CriticalSubActionRow
    | RunCompleteRow
    | ApplicationQuitRow
    | ExitModuleRow
    | RestartRow
    | SupplySelectionVerifiedRow
  )[]
}
